import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, IconButton, Chip, Button, Avatar, Menu, MenuItem, Skeleton} from '@mui/material'
import {
    ArrowBack,
    MoreHoriz,
    WarningAmberOutlined,
    Check,
    Add,
    Edit,
    Business,
    Storefront,
    Sensors,
    AccountBalance,
    Delete,
    People,
    Send,
    Upload,
    Refresh,
} from '@mui/icons-material'
import {useNavigate, useParams, useLocation, Link as LinkRoute} from 'react-router-dom'
import {format} from 'date-fns'
import {useDropzone} from 'react-dropzone'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {adminApi} from '../../services'
import {parseError} from '../../utils/helpers'
import {TextSkeleton, ChipSkeleton, ButtonSkeleton, skeletonBusyProps} from '../../components'
import {
    BranchEvvDialog,
    DeleteBranchDialog,
    DeletePayerDialog,
    HhaxCredentialsDialog,
    InviteUserDialog,
    PayerDialog,
    PAYER_TYPES,
    SERVICE_CATEGORIES,
} from './components'

const evvChip = required =>
    required ? (
        <Chip label="EVV Required" variant="outlined" color="success" size="small" sx={{backgroundColor: '#2E7D3214'}} />
    ) : (
        <Chip label="No EVV" variant="outlined" size="small" sx={{color: 'text.secondary', borderColor: 'divider', backgroundColor: '#00000014'}} />
    )

// A payer is only reportable to HHAeXchange with its EVV payer code, so a
// missing one is worth saying on the branch that bills it. code_required is
// advisory — an inactive or non-Medicaid payer has no code to hold.
const payerEvvChip = hhax => {
    if (!hhax?.code_required) return null
    return hhax?.payer_code ? (
        <Chip label={`EVV code: ${hhax.payer_code}`} variant="outlined" color="success" size="small" sx={{backgroundColor: '#2E7D320A'}} />
    ) : (
        <Chip
            icon={<WarningAmberOutlined fontSize="small" />}
            label="EVV code missing"
            variant="outlined"
            color="error"
            size="small"
            sx={{backgroundColor: '#D32F2F0A'}}
        />
    )
}

const branchEvvChip = hhax =>
    hhax?.enabled ? (
        <Chip
            icon={<Check fontSize="small" />}
            label="EVV enabled"
            variant="outlined"
            color="success"
            size="small"
            sx={{backgroundColor: '#2E7D320A'}}
        />
    ) : (
        <Chip label="EVV not enabled" variant="outlined" size="small" sx={{color: 'text.secondary', borderColor: 'divider'}} />
    )

// The credential is the agency's, not the branch's: one login covers every
// branch, and each branch is told apart by its office identifier instead.
//
// Only a credential HHAeXchange has actually accepted reads as connected. One
// saved while HHAX was unreachable exists, but nothing has shown it works.
const credentialChip = credential => {
    if (!credential?.configured)
        return <Chip label="Not configured" variant="outlined" color="error" size="small" sx={{backgroundColor: '#D32F2F0A'}} />
    if (credential?.using_default)
        return <Chip label="Using Careviah's shared credential" variant="outlined" color="warning" size="small" sx={{backgroundColor: '#ED6C020A'}} />
    switch (credential?.check_status) {
        case 'verified':
            return (
                <Chip
                    icon={<Check fontSize="small" />}
                    label="Connected"
                    variant="outlined"
                    color="success"
                    size="small"
                    sx={{backgroundColor: '#2E7D320A'}}
                />
            )
        case 'rejected':
            return <Chip label="Rejected by HHAeXchange" variant="outlined" color="error" size="small" sx={{backgroundColor: '#D32F2F0A'}} />
        case 'unreachable':
            return <Chip label="Saved, not verified" variant="outlined" color="warning" size="small" sx={{backgroundColor: '#ED6C020A'}} />
        default:
            return <Chip label="Not checked" variant="outlined" color="warning" size="small" sx={{backgroundColor: '#ED6C020A'}} />
    }
}

// What the last check found, in words an operator can act on.
const checkSummary = credential => {
    if (!credential?.checked_at) return 'Never checked'
    const when = format(new Date(credential.checked_at), 'MMM d, yyyy h:mm a')
    switch (credential?.check_status) {
        case 'verified':
            return `${when} — accepted by HHAeXchange`
        case 'rejected':
            return `${when} — HHAeXchange refused these credentials. Rotate them.`
        default:
            return `${when} — HHAeXchange could not be reached. Check again later.`
    }
}

const payorStatusChip = connected =>
    connected ? (
        <Chip
            icon={<Check fontSize="small" />}
            label="MN-ITS login set"
            variant="outlined"
            color="success"
            size="small"
            sx={{backgroundColor: '#2E7D320A'}}
        />
    ) : (
        <Chip label="MN-ITS login not set" variant="outlined" size="small" sx={{color: 'text.secondary', borderColor: 'divider'}} />
    )

// The breadcrumb, the page title and the header card all name the organization
// before these are read, so the labels do not repeat it. "EVV" alone here and
// "EVV (HHAeXchange)" on the panel: the aggregator's name is worth saying where
// there is room for it, not in a narrow rail.
const tabs = [
    {key: 'information', label: 'General', icon: <Business fontSize="small" />},
    {key: 'branch', label: 'Branches', icon: <Storefront fontSize="small" />},
    {key: 'users', label: 'Users', icon: <People fontSize="small" />},
    {key: 'payors', label: 'Payors', icon: <AccountBalance fontSize="small" />},
    {key: 'evv', label: 'EVV', icon: <Sensors fontSize="small" />},
]

const labelOf = (options, value) => options.find(option => option.value === value)?.label || value || '—'

// An invited user cannot sign in until they accept, which is worth saying on
// the row rather than leaving "pending" to be interpreted.
const userStatusChip = status => {
    if (status === 'active')
        return (
            <Chip
                icon={<Check fontSize="small" />}
                label="Active"
                variant="outlined"
                color="success"
                size="small"
                sx={{backgroundColor: '#2E7D320A'}}
            />
        )
    if (status === 'pending_invitation')
        return <Chip label="Invitation sent" variant="outlined" color="warning" size="small" sx={{backgroundColor: '#ED6C020A'}} />
    return <Chip label={status || 'Unknown'} variant="outlined" size="small" sx={{color: 'text.secondary', borderColor: 'divider'}} />
}

const environmentLabel = baseUrl => {
    if (!baseUrl) return '—'
    if (baseUrl.includes('implementation')) return 'Implementation (sandbox)'
    if (baseUrl.includes('cloud')) return 'Production'
    return baseUrl
}

// Placeholders for the tabs' lists, drawn with the same classes the loaded rows use.
const RowsSkeleton = ({count = 3, trailing}) => (
    <div className="payors-list">
        {Array.from({length: count}).map((_, index) => (
            <div className="payor-row" key={index}>
                <div className="payor-info">
                    <TextSkeleton width={[150, 120, 170][index % 3]} fontSize={14} />
                    <TextSkeleton width={[240, 200, 260][index % 3]} fontSize={14} />
                </div>
                <div className="payor-chips">{trailing}</div>
            </div>
        ))}
    </div>
)

const BranchCardsSkeleton = () => (
    <>
        {[0, 1].map(index => (
            <div className="card" key={index}>
                <div className="branch-header">
                    <div className="top">
                        <div className="top-left">
                            <TextSkeleton width={index ? 120 : 160} fontSize={16} />
                            <TextSkeleton width={70} fontSize={12} />
                        </div>
                        <div className="top-right">
                            <Skeleton variant="circular" width={24} height={24} sx={{m: 1}} />
                        </div>
                    </div>
                </div>
                <br />
                <div className="content">
                    <TextSkeleton width={index ? 280 : 340} />
                </div>
                <br />
                <div className="content">
                    <TextSkeleton width={index ? 220 : 260} />
                </div>
                <br />
                <div className="content">
                    <TextSkeleton width={150} fontSize={16} />
                    <ChipSkeleton width={100} sx={{ml: 0.75}} />
                </div>
                <br />
                <div className="field-stack">
                    <TextSkeleton width={120} fontSize={16} />
                    <TextSkeleton width={index ? 240 : 320} />
                </div>
                <br />
                <div className="field-stack">
                    <TextSkeleton width={60} fontSize={12} />
                    <div className="services-row">
                        {[90, 110].map(width => (
                            <div className="service-box" key={width}>
                                <TextSkeleton width={width} />
                                <ChipSkeleton width={96} />
                            </div>
                        ))}
                    </div>
                </div>
                <br />
                <div className="field-stack">
                    <TextSkeleton width={56} fontSize={12} />
                    <RowsSkeleton count={2} trailing={<ChipSkeleton width={120} />} />
                </div>
            </div>
        ))}
    </>
)

// SkeletonContainer's busy-region attributes, put on the real container so its
// buttons are not remounted (and a click lost) when the data lands.

// Stands in for one value of a details row until the organization arrives.
const valueOr = (skeleton, width, value) => (skeleton ? <TextSkeleton width={width} fontSize={16} /> : value)

export default () => {
    const {organizationId} = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const {setLoading, setMessageBox} = useGlobalStates()
    const [organization, setOrganization] = useState(null)
    const [branches, setBranches] = useState([])
    const [activeTab, setActiveTab] = useState('information')
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedBranch, setSelectedBranch] = useState(null)
    const [openDeleteBranch, setOpenDeleteBranch] = useState(false)
    const [openHhaxCredentials, setOpenHhaxCredentials] = useState(false)
    const [evvBranch, setEvvBranch] = useState(null)
    const [users, setUsers] = useState([])
    const [roles, setRoles] = useState([])
    const [openInviteUser, setOpenInviteUser] = useState(false)
    const [payers, setPayers] = useState([])
    const [selectedPayer, setSelectedPayer] = useState(null)
    const [openPayer, setOpenPayer] = useState(false)
    const [openDeletePayer, setOpenDeletePayer] = useState(false)
    const [uploadingLogo, setUploadingLogo] = useState(false)
    const [skeleton, setSkeleton] = useState(true)

    const loadOrganization = () => adminApi.getOrganization(organizationId).then(({data: {data}}) => setOrganization(data))

    const {
        getRootProps: getLogoRootProps,
        getInputProps: getLogoInputProps,
        open: openLogoPicker,
    } = useDropzone({
        // No SVG: it can carry script, and the mobile app can't render it.
        accept: {'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/gif': ['.gif'], 'image/webp': ['.webp']},
        noClick: true,
        noKeyboard: true,
        multiple: false,
        maxSize: 3 * 1024 * 1024,
        onDrop: acceptedFiles => {
            const file = acceptedFiles[0]
            if (!file) return
            const formData = new FormData()
            formData.append('file', file)
            setUploadingLogo(true)
            adminApi
                .uploadOrganizationLogo(organizationId, formData)
                .then(({data: {data}}) => {
                    setUploadingLogo(false)
                    setOrganization(data)
                })
                .catch(error => {
                    setUploadingLogo(false)
                    const {title, description} = parseError(error)
                    setMessageBox({status: 'error', title: title || 'Logo upload failed', description})
                })
        },
    })

    const checkCredentials = () => {
        setLoading(true)
        adminApi
            .verifyHhaxCredentials(organizationId)
            .then(({data: {data}}) => {
                setLoading(false)
                const verified = data?.check_status === 'verified'
                setMessageBox(
                    verified
                        ? {status: 'success', title: 'Connected', description: 'HHAeXchange accepted these credentials.'}
                        : {
                              status: data?.check_status === 'rejected' ? 'error' : 'warning',
                              title: data?.check_status === 'rejected' ? 'Credentials refused' : 'Could not reach HHAeXchange',
                              description: data?.check_error || 'The credentials could not be verified.',
                          },
                )
                loadOrganization()
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                setMessageBox({
                    status: 'error',
                    title: title || 'Could not check credentials',
                    description: description || 'There was an error checking these credentials. Please try again later.',
                })
            })
    }

    const loadBranches = () => adminApi.listBranches(organizationId).then(({data: {data}}) => setBranches(data?.branches || []))

    const loadUsers = () =>
        adminApi.listOrgUsers(organizationId, {include_roles: true, limit: 100}).then(({data: {data}}) => setUsers(data?.users || []))

    const loadRoles = () => adminApi.listOrgRoles(organizationId).then(({data: {data}}) => setRoles(data?.roles || []))

    const loadPayers = () => adminApi.listPayers(organizationId).then(({data: {data}}) => setPayers(data?.data || data?.payers || []))

    // A payer change reaches the branch cards too: every active branch bills the
    // organization's payers, so the branch list is reloaded alongside.
    const payersChanged = () => Promise.all([loadPayers(), loadBranches()])

    useEffect(() => {
        setSkeleton(true)
        Promise.all([loadOrganization(), loadBranches(), loadPayers(), loadUsers(), loadRoles()])
            .then(() => setSkeleton(false))
            .catch(error => {
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }, [organizationId])

    useEffect(() => {
        if (location.state?.toast) {
            setMessageBox(location.state.toast)
            if (location.state?.tab) setActiveTab(location.state.tab)
            navigate(location.pathname, {replace: true, state: {}})
        }
    }, [])

    const resendInvitation = member => {
        setLoading(true)
        adminApi
            .resendOrgUserInvitation(organizationId, member.id)
            .then(() => {
                setLoading(false)
                setMessageBox({
                    status: 'success',
                    title: 'Invitation sent again',
                    description: `${member.email} has a fresh link to set their password.`,
                })
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                setMessageBox({
                    status: 'error',
                    title: title || 'Could not resend the invitation',
                    description: description || 'There was an error resending this invitation. Please try again later.',
                })
            })
    }

    const menuOpen = (e, branch) => {
        setAnchorEl(e.currentTarget)
        setSelectedBranch(branch)
    }

    const menuClose = () => setAnchorEl(null)

    const updateBranch = () => {
        menuClose()
        navigate(`/admin/organizations/${organizationId}/branches/${selectedBranch?.id}/edit`)
    }

    const deleteBranchClick = () => {
        menuClose()
        setOpenDeleteBranch(true)
    }

    if (!skeleton && !organization) {
        return (
            <Typography variant="h6" color="text.primary">
                Loading organization…
            </Typography>
        )
    }

    return (
        <>
            <Typography variant="body1" color="text.secondary">
                <LinkRoute className="link" to="/admin/organizations">
                    Organizations
                </LinkRoute>
                {skeleton ? (
                    <>
                        {' / '}
                        <Skeleton width={140} sx={{display: 'inline-block', verticalAlign: 'middle'}} />
                    </>
                ) : (
                    ` / ${organization?.name || 'Organization Details'}`
                )}
            </Typography>
            <br />
            <div className="header">
                <div className="back-button" onClick={() => navigate('/admin/organizations')}>
                    <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                </div>
                <Typography variant="h5" color="text.primary">
                    Organization Details
                </Typography>
            </div>
            <br />
            <br />
            <div className="title-card">
                {skeleton ? (
                    <>
                        <Skeleton variant="rounded" width={40} height={40} />
                        <TextSkeleton width={200} fontSize={20} />
                    </>
                ) : (
                    <>
                        <Avatar
                            src={organization?.logo_url}
                            variant="rounded"
                            style={{
                                width: 40,
                                height: 40,
                                backgroundColor: organization?.logo_url ? '#ffffff' : '#0d5d5933',
                                color: '#0d5d59',
                                fontWeight: 600,
                            }}
                            slotProps={{img: {style: {objectFit: 'contain', padding: 4}}}}>
                            {organization?.name?.charAt(0)}
                        </Avatar>
                        <Typography variant="h6" color="text.primary">
                            {organization?.name}
                        </Typography>
                    </>
                )}
            </div>
            <br />
            <br />
            <div className="content-container">
                <div className="tab-list">
                    {tabs.map(tab => (
                        <div key={tab.key} className={`tab-item ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)}>
                            <div className="icon">{tab.icon}</div>
                            <div className="label">{tab.label}</div>
                        </div>
                    ))}
                </div>
                <div className="tab-content" {...skeletonBusyProps(skeleton)}>
                    {activeTab === 'information' && (
                        <div className="branch-panel">
                            <div className="panel-header">
                                <Typography variant="h6" color="text.primary">
                                    General
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<Edit />}
                                    onClick={() => navigate(`/admin/organizations/${organizationId}/edit`)}>
                                    Edit
                                </Button>
                            </div>
                            <br />
                            <div className="form" style={{height: 80, alignItems: 'center'}}>
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Logo
                                </Typography>
                                <div {...getLogoRootProps()} style={{display: 'flex', alignItems: 'center', gap: 12}}>
                                    <input {...getLogoInputProps()} />
                                    {skeleton ? (
                                        <Skeleton variant="rounded" width={80} height={80} />
                                    ) : organization?.logo_url ? (
                                        <img src={organization.logo_url} height={80} alt="Organization logo" />
                                    ) : (
                                        <Avatar variant="rounded" style={{width: 80, height: 80, backgroundColor: '#0d5d5933', color: '#0d5d59'}}>
                                            {organization?.name?.charAt(0)}
                                        </Avatar>
                                    )}
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Upload fontSize="small" />}
                                        onClick={openLogoPicker}
                                        disabled={uploadingLogo || skeleton}>
                                        {uploadingLogo ? 'Uploading…' : organization?.logo_url ? 'Replace' : 'Upload logo'}
                                    </Button>
                                </div>
                            </div>
                            <div className="form">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Healthcare Name
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 180, organization?.name)}
                                </Typography>
                            </div>
                            <div className="form">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Email
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 200, organization?.email)}
                                </Typography>
                            </div>
                            <div className="form">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Phone Number
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 120, organization?.phone)}
                                </Typography>
                            </div>
                            <div className="form" style={{alignItems: 'start'}}>
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Business Address
                                </Typography>
                                {skeleton ? (
                                    <div style={{width: 260}}>
                                        <TextSkeleton width="100%" fontSize={16} />
                                        <TextSkeleton width="55%" fontSize={16} />
                                    </div>
                                ) : (
                                    <Typography variant="body1" color="text.primary" style={{maxWidth: 300}}>
                                        {[
                                            organization?.address?.street,
                                            organization?.address?.city,
                                            organization?.address?.state,
                                            organization?.address?.zip,
                                        ]
                                            .filter(Boolean)
                                            .join(', ')}
                                    </Typography>
                                )}
                            </div>
                            <br />
                            <div className="form">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Federal Tax ID (EIN)
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 110, organization?.federal_tax_id)}
                                </Typography>
                            </div>
                            <div className="form">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Organization NPI
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 110, organization?.npi)}
                                </Typography>
                            </div>
                            <div className="form">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    US Taxpayer ID (TIN)
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 110, organization?.tin)}
                                </Typography>
                            </div>
                            <br />
                            <div className="divider" />
                            <br />
                            <Typography variant="h7" color="primary.dark">
                                Contact Person
                            </Typography>
                            <br />
                            <br />
                            <div className="form">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Name
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 150, organization?.contact_name)}
                                </Typography>
                            </div>
                            <div className="form">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Phone Number
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 120, organization?.contact_phone)}
                                </Typography>
                            </div>
                            <div className="form">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Email
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 190, organization?.contact_email)}
                                </Typography>
                            </div>
                        </div>
                    )}
                    {activeTab === 'branch' && (
                        <div className="branch-panel">
                            <div className="panel-header">
                                <Typography variant="h6" color="text.primary">
                                    Branches
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<Add />}
                                    onClick={() => navigate(`/admin/organizations/${organizationId}/branches/add`)}>
                                    Add Branch
                                </Button>
                            </div>
                            <br />
                            {skeleton && <BranchCardsSkeleton />}
                            {!skeleton &&
                                branches?.map(branch => {
                                    // A payer with no EVV code cannot have its visits reported, which is the
                                    // thing this screen exists to set up. The MN-ITS login is not used until
                                    // billing exists, so it is not what the header warns about.
                                    const missingEvvCode =
                                        branch?.payers?.filter(payer => payer?.hhax?.code_required && !payer?.hhax?.payer_code)?.length || 0

                                    return (
                                        <div key={branch.id} className="card">
                                            <div className="branch-header">
                                                <div className="top">
                                                    <div className="top-left">
                                                        <div className="top-name-row">
                                                            <Typography variant="h8" color="primary">
                                                                {branch?.branch_name}
                                                            </Typography>
                                                            {missingEvvCode > 0 && (
                                                                <Chip
                                                                    icon={<WarningAmberOutlined fontSize="small" />}
                                                                    label={`${missingEvvCode} payor${missingEvvCode > 1 ? 's' : ''} without an EVV code`}
                                                                    variant="outlined"
                                                                    color="error"
                                                                    size="small"
                                                                    sx={{backgroundColor: '#D32F2F0A'}}
                                                                />
                                                            )}
                                                        </div>
                                                        <Typography variant="h10" color="text.primary">
                                                            {branch?.branch_code}
                                                        </Typography>
                                                    </div>
                                                    <div className="top-right">
                                                        <IconButton onClick={e => menuOpen(e, branch)}>
                                                            <MoreHoriz />
                                                        </IconButton>
                                                    </div>
                                                </div>
                                            </div>
                                            <br />
                                            <div className="content">
                                                <Typography variant="h8" color="text.primary" style={{marginRight: 6}}>
                                                    Email:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {branch?.email}
                                                </Typography>
                                                <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                                    |
                                                </Typography>
                                                <Typography variant="h9" color="text.primary" style={{marginRight: 6}}>
                                                    Phone Number:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {branch?.phone_number}
                                                </Typography>
                                            </div>
                                            <br />
                                            <div className="content">
                                                <Typography variant="h8" color="text.primary" style={{marginRight: 6}}>
                                                    Branch NPI:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {branch?.provider_number || '—'}
                                                </Typography>
                                                <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                                    |
                                                </Typography>
                                                <Typography variant="h9" color="text.primary" style={{marginRight: 6}}>
                                                    UMPI:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {branch?.umpi || '—'}
                                                </Typography>
                                            </div>
                                            <br />
                                            <div className="content">
                                                <Typography variant="h8" color="text.primary" style={{marginRight: 6}}>
                                                    EVV (HHAeXchange):
                                                </Typography>
                                                {branchEvvChip(branch?.hhax)}
                                                {branch?.hhax?.office_identifier && (
                                                    <>
                                                        <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                                            |
                                                        </Typography>
                                                        <Typography variant="h9" color="text.primary" style={{marginRight: 6}}>
                                                            Office ID:
                                                        </Typography>
                                                        <Typography variant="body2" color="text.primary">
                                                            {branch?.hhax?.office_qualifier} {branch?.hhax?.office_identifier}
                                                        </Typography>
                                                    </>
                                                )}
                                            </div>
                                            <br />
                                            <div className="field-stack">
                                                <Typography variant="h8" color="text.primary">
                                                    Branch Address:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {[
                                                        branch?.addresses?.[0]?.street_line1,
                                                        branch?.addresses?.[0]?.street_line2,
                                                        branch?.addresses?.[0]?.city,
                                                        branch?.addresses?.[0]?.state,
                                                        branch?.addresses?.[0]?.zip_code,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(', ')}
                                                </Typography>
                                            </div>
                                            <br />
                                            <div className="field-stack">
                                                <Typography variant="overline" color="text.secondary" className="section-label">
                                                    Services
                                                </Typography>
                                                {!branch?.services?.length && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        No services — a branch with none cannot be scheduled against.
                                                    </Typography>
                                                )}
                                                <div className="services-row">
                                                    {branch?.services?.map((service, index) => (
                                                        <div className="service-box" key={`${service.id}-${index}`}>
                                                            <Typography variant="h9" color="text.primary">
                                                                {service?.name}
                                                            </Typography>
                                                            {evvChip(service?.evv_required)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <br />
                                            <div className="field-stack">
                                                <Typography variant="overline" color="text.secondary" className="section-label">
                                                    Payors
                                                </Typography>
                                                {!branch?.payers?.length && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        No payors — add one on the Payors tab, and every active branch can bill it.
                                                    </Typography>
                                                )}
                                                <div className="payors-list">
                                                    {branch?.payers?.map(payer => (
                                                        <div className="payor-row" key={payer.id}>
                                                            <div className="payor-info">
                                                                <Typography variant="h9" color="text.primary">
                                                                    {payer?.payer_name}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.primary">
                                                                    ID: {payer?.provider_number}
                                                                </Typography>
                                                            </div>
                                                            <div className="payor-chips">
                                                                {payerEvvChip(payer?.hhax)}
                                                                {payorStatusChip(Boolean(payer?.username))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                        </div>
                    )}
                    {activeTab === 'users' && (
                        <div className="branch-panel">
                            <div className="panel-header">
                                <Typography variant="h6" color="text.primary">
                                    Users
                                </Typography>
                                <Button variant="outlined" startIcon={<Add />} onClick={() => setOpenInviteUser(true)}>
                                    Invite User
                                </Button>
                            </div>
                            <br />
                            {skeleton ? (
                                <RowsSkeleton count={3} trailing={<ChipSkeleton width={80} />} />
                            ) : users?.length ? (
                                <div className="payors-list">
                                    {users.map(member => (
                                        <div className="payor-row" key={member.id}>
                                            <div className="payor-info">
                                                <Typography variant="h9" color="text.primary">
                                                    {[member?.first_name, member?.last_name].filter(Boolean).join(' ') || member?.email}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {member?.email}
                                                    {member?.roles?.length ? ` · ${member.roles.join(', ')}` : ' · no role'}
                                                </Typography>
                                            </div>
                                            <div className="payor-chips">
                                                {userStatusChip(member?.status)}
                                                {member?.status === 'pending_invitation' && (
                                                    <Button
                                                        size="small"
                                                        startIcon={<Send fontSize="small" />}
                                                        onClick={() => resendInvitation(member)}>
                                                        Resend
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <Typography variant="h9" color="text.primary">
                                        Nobody can sign in yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Invite this organization's first administrator. They receive an email with a link to set their own password,
                                        and can then invite the rest of their staff themselves.
                                    </Typography>
                                </div>
                            )}
                        </div>
                    )}
                    {activeTab === 'payors' && (
                        <div className="branch-panel">
                            <div className="panel-header">
                                <Typography variant="h6" color="text.primary">
                                    Payors
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<Add />}
                                    onClick={() => {
                                        setSelectedPayer(null)
                                        setOpenPayer(true)
                                    }}>
                                    Add Payor
                                </Button>
                            </div>
                            <br />
                            {skeleton ? (
                                <RowsSkeleton
                                    count={3}
                                    trailing={
                                        <>
                                            <ChipSkeleton width={110} />
                                            <Skeleton variant="circular" width={20} height={20} sx={{m: 1}} />
                                            <Skeleton variant="circular" width={20} height={20} sx={{m: 1}} />
                                        </>
                                    }
                                />
                            ) : payers?.length ? (
                                <div className="payors-list">
                                    {payers.map(payer => (
                                        <div className="payor-row" key={payer.id}>
                                            <div className="payor-info">
                                                <Typography variant="h9" color="text.primary">
                                                    {payer?.payer_name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {labelOf(PAYER_TYPES, payer?.payer_type)} · {labelOf(SERVICE_CATEGORIES, payer?.service_category)}
                                                    {payer?.provider_number ? ` · ID: ${payer.provider_number}` : ''}
                                                </Typography>
                                            </div>
                                            <div className="payor-chips">
                                                {payerEvvChip(payer?.hhax)}
                                                <IconButton
                                                    onClick={() => {
                                                        setSelectedPayer(payer)
                                                        setOpenPayer(true)
                                                    }}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => {
                                                        setSelectedPayer(payer)
                                                        setOpenDeletePayer(true)
                                                    }}>
                                                    <Delete fontSize="small" color="error" />
                                                </IconButton>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <Typography variant="h9" color="text.primary">
                                        No payors yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        A payor is who pays for a visit, and carries the EVV code sent to HHAeXchange. Until this organization has
                                        one, its branches cannot bill and no visit can be reported.
                                    </Typography>
                                </div>
                            )}
                        </div>
                    )}
                    {activeTab === 'evv' && (
                        <div className="branch-panel">
                            <div className="panel-header">
                                <Typography variant="h6" color="text.primary">
                                    EVV (HHAeXchange)
                                </Typography>
                                <div className="payor-chips">
                                    {skeleton && <ButtonSkeleton width={170} />}
                                    {!skeleton && organization?.hhax?.credential?.configured && !organization?.hhax?.credential?.using_default && (
                                        <Button variant="text" startIcon={<Refresh />} onClick={checkCredentials}>
                                            Check connection
                                        </Button>
                                    )}
                                    {!skeleton && (
                                        <Button variant="outlined" startIcon={<Edit />} onClick={() => setOpenHhaxCredentials(true)}>
                                            {organization?.hhax?.credential?.configured && !organization?.hhax?.credential?.using_default
                                                ? 'Rotate credentials'
                                                : 'Set credentials'}
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <br />
                            <Typography variant="h7" color="primary.dark">
                                Connection
                            </Typography>
                            <br />
                            <br />
                            <div className="form evv-row">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Status
                                </Typography>
                                <div className="form-value">
                                    {skeleton ? <ChipSkeleton width={96} /> : credentialChip(organization?.hhax?.credential)}
                                </div>
                            </div>
                            <div className="form evv-row">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Client ID
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 260, organization?.hhax?.credential?.client_id || '—')}
                                </Typography>
                            </div>
                            <div className="form evv-row">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Client Secret
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.secondary">
                                    {valueOr(skeleton, 150, organization?.hhax?.credential?.configured ? 'Stored — never shown' : 'Not set')}
                                </Typography>
                            </div>
                            <div className="form evv-row">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Environment
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 180, environmentLabel(organization?.hhax?.credential?.base_url))}
                                </Typography>
                            </div>
                            <div className="form evv-row">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    API Version
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 40, organization?.hhax?.credential?.api_version || '—')}
                                </Typography>
                            </div>
                            <div className="form evv-row">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Last Rotated
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(
                                        skeleton,
                                        160,
                                        organization?.hhax?.credential?.last_rotated_at
                                            ? format(new Date(organization.hhax.credential.last_rotated_at), 'MMM d, yyyy h:mm a')
                                            : '—',
                                    )}
                                </Typography>
                            </div>
                            {organization?.hhax?.credential?.configured && !organization?.hhax?.credential?.using_default && (
                                <div className="form evv-row">
                                    <Typography className="form-label" variant="h8" color="text.primary">
                                        Last Checked
                                    </Typography>
                                    <Typography
                                        className="form-value"
                                        variant="body1"
                                        color={organization?.hhax?.credential?.check_status === 'verified' ? 'text.primary' : 'text.secondary'}>
                                        {checkSummary(organization?.hhax?.credential)}
                                    </Typography>
                                </div>
                            )}
                            <br />
                            <div className="divider" />
                            <br />
                            <Typography variant="h7" color="primary.dark">
                                Provider Identity
                            </Typography>
                            <br />
                            <br />
                            <div className="form evv-row">
                                <Typography className="form-label" variant="h8" color="text.primary">
                                    Federal Tax ID (EIN)
                                </Typography>
                                <Typography className="form-value" variant="body1" color="text.primary">
                                    {valueOr(skeleton, 110, organization?.federal_tax_id || '—')}
                                </Typography>
                            </div>
                            <Typography variant="body2" color="text.secondary">
                                Sent as the provider tax ID on every visit and caregiver. Edit it on the General tab.
                            </Typography>
                            <br />
                            <div className="divider" />
                            <br />
                            <Typography variant="h7" color="primary.dark">
                                Branch Enrolment
                            </Typography>
                            <br />
                            <br />
                            <Typography variant="body2" color="text.secondary">
                                One credential covers the whole organization. Each branch is told apart by its own office identifier, and only
                                enrolled branches report their visits.
                            </Typography>
                            <br />
                            {skeleton ? (
                                <RowsSkeleton
                                    count={2}
                                    trailing={
                                        <>
                                            <ChipSkeleton width={110} />
                                            <ButtonSkeleton width={80} />
                                        </>
                                    }
                                />
                            ) : (
                                <div className="payors-list">
                                    {branches?.map(branch => (
                                        <div className="payor-row" key={branch.id}>
                                            <div className="payor-info">
                                                <Typography variant="h9" color="text.primary">
                                                    {branch?.branch_name}
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {branch?.hhax?.office_identifier
                                                        ? `${branch?.hhax?.office_qualifier} ${branch?.hhax?.office_identifier}`
                                                        : 'No office identifier'}
                                                </Typography>
                                            </div>
                                            <div className="payor-chips">
                                                {branchEvvChip(branch?.hhax)}
                                                <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => setEvvBranch(branch)}>
                                                    {branch?.hhax?.enabled ? 'Edit' : 'Enrol'}
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <br />
            <br />
            <br />
            <br />
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                <MenuItem onClick={updateBranch}>Update branch</MenuItem>
                <MenuItem onClick={deleteBranchClick} sx={theme => ({color: theme.palette.error.main})}>
                    Delete branch
                </MenuItem>
            </Menu>
            <InviteUserDialog
                open={openInviteUser}
                setOpen={setOpenInviteUser}
                organization={organization}
                roles={roles}
                branches={branches}
                onInvited={loadUsers}
            />
            <PayerDialog open={openPayer} setOpen={setOpenPayer} organization={organization} payer={selectedPayer} onSaved={payersChanged} />
            <DeletePayerDialog
                open={openDeletePayer}
                setOpen={setOpenDeletePayer}
                organization={organization}
                payer={selectedPayer}
                onDeleted={payersChanged}
            />
            <BranchEvvDialog
                open={Boolean(evvBranch)}
                setOpen={value => !value && setEvvBranch(null)}
                organization={organization}
                branch={evvBranch}
                onSaved={loadBranches}
            />
            <HhaxCredentialsDialog
                open={openHhaxCredentials}
                setOpen={setOpenHhaxCredentials}
                organization={organization}
                onSaved={loadOrganization}
            />
            <DeleteBranchDialog
                open={openDeleteBranch}
                setOpen={setOpenDeleteBranch}
                organization={organization}
                branch={selectedBranch}
                onDeleted={loadBranches}
            />
        </>
    )
}
